// src/components/Layout/Sidebar.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

// AccordionPanel lives OUTSIDE Sidebar — never re-mounted on parent render
const AccordionPanel = ({
  id, label, icon, children,
  topLevel = false,
  isOpen, isCollapsed,
  onToggle, onMouseEnter, onMouseLeave,
}) => (
  <li className={`left-navitem accordion-item${topLevel ? '' : ' inner-accordion-item'}`}>
    <button
      type="button"
      className={`accordion-button${isOpen ? '' : ' collapsed'} ${topLevel ? 'left-accordion-btn-1' : 'inner-accordion-btn-2'}`}
      onClick={() => onToggle(id)}
      disabled={isCollapsed}
      onMouseEnter={topLevel ? onMouseEnter : undefined}
      onMouseLeave={topLevel ? onMouseLeave : undefined}
    >
      {icon && <span className="left-navicon">{icon}</span>}
      <span className="navlink-name">{label}</span>
    </button>

    {/* display:none/block — Bootstrap JS has zero control over this */}
    <div style={{ display: isOpen ? 'block' : 'none' }}>
      {children}
    </div>
  </li>
);

// ─────────────────────────────────────────────────────────────────────────────
const Sidebar = ({ isCollapsed, onLinkClick }) => {  const { user } = useAuth();
  const location = useLocation();
  
  const [openMenus, setOpenMenus] = useState({}); 
  const savedMenus = useRef({});
  const [tooltip, setTooltip] = useState({ visible: false, text: '', top: 0, left: 0 });

  // Check if screen is mobile
  const isMobile = () => window.innerWidth < 992;

  const hasRole = useCallback((roles) => !!user && roles.includes(user.role), [user]);

  const hasPermission = useCallback((perm) => {
    if (!user) return false;
    if (user.role === 'admin' || user.role === 'sub-admin') return true;
    return !!user.permissions?.[perm];
  }, [user]);

  const canManageAdmins       = hasRole(['admin', 'sub-admin','distributor', 'retailer']);
  const canManageUsers        = hasPermission('manageUsers')        || hasRole(['admin', 'sub-admin','distributor', 'retailer']);
  const canManageGames        = hasPermission('manageGames')        || hasRole(['admin']);
  const canViewBets           = hasPermission('manageBets')         || hasRole(['admin', 'sub-admin', 'distributor', 'retailer']);
  const canViewResults        = hasPermission('manageResults')      || hasRole(['admin', 'sub-admin', 'distributor', 'retailer']);
  const canManageSettings     = hasPermission('manageSettings')     || hasRole(['admin', 'sub-admin','distributor', 'retailer']);
  const canViewReports        = hasPermission('viewReports')        || hasRole(['admin', 'sub-admin', 'distributor', 'retailer']);
  const canManageTransactions = hasPermission('manageTransactions') || hasRole(['admin', 'sub-admin', 'distributor', 'retailer']);

  // Save open state when collapsing, restore when expanding
  useEffect(() => {
    if (isCollapsed) {
      savedMenus.current = { ...openMenus };
      setOpenMenus({});
    } else {
      setOpenMenus({ ...savedMenus.current });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCollapsed]);

  const toggleMenu = (key) => {
    if (isCollapsed) return;
    setOpenMenus(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleMouseEnter = useCallback((e) => {
    if (!isCollapsed) return;
    const el   = e.currentTarget;
    const text = el.querySelector('.navlink-name')?.innerText || el.innerText.trim();
    const rect = el.getBoundingClientRect();
    setTooltip({ visible: true, text, top: rect.top + rect.height / 2, left: rect.right + 15 });
  }, [isCollapsed]);

  const handleMouseLeave = useCallback(() => {
    setTooltip(t => ({ ...t, visible: false }));
  }, []);

  // Handle link click - close sidebar on mobile
  const handleLinkClick = useCallback(() => {
    if (isMobile() && !isCollapsed) {
      onLinkClick?.();
    }
  }, [isCollapsed, onLinkClick]);

  // Build props for a panel by key
  const ap = (id) => ({
    id,
    isOpen:       !!openMenus[id],
    isCollapsed,
    onToggle:     toggleMenu,
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave,
  });

  return (
    <>
      <aside className={`aside-section${isCollapsed ? ' aside-small-section' : ''}`}>
        <div className="admin-logo">
          <img src="/img/royalking-logotext.png" alt="logo" className="logo-desktop img-fluid" />
          <img src="/img/royal-king-logo.png" alt="logo" className="logo-mobile  img-fluid" />
        </div>

        <ul className="left-navbar accordion">

          {/* Dashboard */}
          <li className="left-navitem">
            <NavLink to="/dashboard" className="left-navlink"
              onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}
              onClick={handleLinkClick}>
              <span className="left-navicon"><img src="/img/dasboard-icon.png" alt="icon" /></span>
              <span className="navlink-name">Dashboard</span>
            </NavLink>
          </li>

          {/* Manage User */}
          {canManageAdmins && (
            <AccordionPanel {...ap('manageUser')} topLevel
              label="Manage User" icon={<img src="/img/manage-user-icon.png" alt="" />}>
              <ul className="inner-accordion">

                {hasRole(['admin']) && (
                  <AccordionPanel {...ap('superDistributor')} label="Super Distributor">
                    <ul className="accordion-inner-2">
                      <li><NavLink to="/users/super-distributors" className="accordion-inner-liner" onClick={handleLinkClick}>List Of Super Distributor</NavLink></li>
                      {canViewReports  && <li><NavLink to="/reports/super-distributor" className="accordion-inner-liner" onClick={handleLinkClick}>Business Report</NavLink></li>}
                      {canManageTransactions && <li><NavLink  to="/transactions/transfer/super-distributor"  className="accordion-inner-liner" onClick={handleLinkClick}> Point Transfer</NavLink></li>}
                      {canManageTransactions && <li><NavLink to="/transactions/withdrawals/super-distributor" className="accordion-inner-liner" onClick={handleLinkClick}>Point Withdrawal Requests</NavLink></li>}
                    </ul>
                  </AccordionPanel>
                )}

                {hasRole(['admin', 'sub-admin']) && (
                  <AccordionPanel {...ap('distributor')} label="Distributor">
                    <ul className="accordion-inner-2">
                      <li><NavLink to="/users/distributors" className="accordion-inner-liner" onClick={handleLinkClick}>List Of Distributor</NavLink></li>
                      {canViewReports && <li><NavLink to="/reports/distributor" className="accordion-inner-liner" onClick={handleLinkClick}>Business Report</NavLink></li>}
                      {canManageTransactions && <li><NavLink  to="/transactions/transfer/distributor"  className="accordion-inner-liner" onClick={handleLinkClick}> Point Transfer</NavLink></li>}
                      {canManageTransactions && <li><NavLink to="/transactions/withdrawals/distributor" className="accordion-inner-liner" onClick={handleLinkClick}>Point Withdrawal Requests</NavLink></li>}
                    </ul>
                  </AccordionPanel>
                )}

                {hasRole(['admin', 'sub-admin', "distributor"]) && (
                  <AccordionPanel {...ap('retailer')} label="Retailer">
                    <ul className="accordion-inner-2">
                      <li><NavLink to="/users/retailers" className="accordion-inner-liner" onClick={handleLinkClick}>List Of Retailer</NavLink></li>
                      {canViewReports && <li><NavLink to="/reports/retailer" className="accordion-inner-liner" onClick={handleLinkClick}>Business Report</NavLink></li>}
                      {canManageTransactions && <li><NavLink  to="/transactions/transfer/retailer"  className="accordion-inner-liner" onClick={handleLinkClick}> Point Transfer</NavLink></li>}
                      {canManageTransactions && <li><NavLink to="/transactions/withdrawals/retailer" className="accordion-inner-liner" onClick={handleLinkClick}>Point Withdrawal Requests</NavLink></li>}
                    </ul>
                  </AccordionPanel>
                )}

                {(canManageUsers || canManageAdmins) && (
                  <AccordionPanel {...ap('user')} label="User">
                    <ul className="accordion-inner-2">
                      <li><NavLink to="/users/list" className="accordion-inner-liner" onClick={handleLinkClick}>List Of User</NavLink></li>
                      {canViewReports && <li><NavLink to="/reports/users" className="accordion-inner-liner" onClick={handleLinkClick}>Business Report</NavLink></li>}
                      {canManageTransactions && <li><NavLink  to="/transactions/transfer/user"  className="accordion-inner-liner" onClick={handleLinkClick}> Point Transfer</NavLink></li>}
                      {canManageTransactions && <li><NavLink to="/transactions/withdrawals/user" className="accordion-inner-liner" onClick={handleLinkClick}>Point Withdrawal Requests</NavLink></li>}
                    </ul>
                  </AccordionPanel>
                )}

              </ul>
            </AccordionPanel>
          )}

          {/* Game Management */}
          {hasRole(['admin']) && (
            <AccordionPanel {...ap('gameManagement')} topLevel
              label="Game Management" icon={<img src="/img/game-management-icon.png" alt="" />}>
              <ul className="inner-accordion">

                {hasRole(['admin', 'sub-admin']) && (
                  <AccordionPanel {...ap('gameRounds')} label="Game Rounds">
                    <ul className="accordion-inner-2">
                      <li><NavLink to="/game/time-setting" className="accordion-inner-liner" onClick={handleLinkClick}>Time Setting</NavLink></li>
                    </ul>
                  </AccordionPanel>
                )}

                {hasRole(['admin', 'sub-admin', 'distributor']) && (
                  <AccordionPanel {...ap('gameResult')} label="Game Result">
                    <ul className="accordion-inner-2">
                      {hasRole(['admin', 'sub-admin']) && (
                        <li><NavLink to="/game/auto-result"  className="accordion-inner-liner" onClick={handleLinkClick}>Auto Result</NavLink></li>
                      )}
                      <li><NavLink to="/game/manual-result" className="accordion-inner-liner" onClick={handleLinkClick}>Manual Result</NavLink></li>
                    </ul>
                  </AccordionPanel>
                )}

              </ul>
            </AccordionPanel>
          )}

          {/* Bet History */}
          {canViewBets && (
            <li className="left-navitem">
              <NavLink to="/bets/history" className="left-navlink"
                onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}
                onClick={handleLinkClick}>
                <span className="left-navicon"><img src="/img/bet-history-icon.png" alt="icon" /></span>
                <span className="navlink-name">Bet History</span>
              </NavLink>
            </li>
          )}

          {/* Result History */}
          {canViewResults && (
            <li className="left-navitem">
              <NavLink to="/results/history" className="left-navlink"
                onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}
                onClick={handleLinkClick}>
                <span className="left-navicon"><img src="/img/result-history-icon.png" alt="icon" /></span>
                <span className="navlink-name">Result History</span>
              </NavLink>
            </li>
          )}

          {/* Game Round History */}
          {hasRole(['admin']) && (
            <li className="left-navitem">
              <NavLink to="/games/round-history" className="left-navlink"
                onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}
                onClick={handleLinkClick}>
                <span className="left-navicon"><img src="/img/game-round-history-icon.png" alt="icon" /></span>
                <span className="navlink-name">Game Round History</span>
              </NavLink>
            </li>
          )}

          {/* Settings */}
            {canManageSettings && (
              <AccordionPanel {...ap('setting')} topLevel
                label="Setting" icon={<img src="/img/setting-icon.png" alt="" />}>
                <ul className="inner-accordion">
                  <li className="accordion-item inner-accordion-item">
                    <NavLink to="/settings/change-password" className="inner-accordion-link-2" onClick={handleLinkClick}>Change Password</NavLink>
                  </li>
                  
                  {user?.role !== 'admin' ? (
                  <li className="accordion-item inner-accordion-item">
                    <NavLink to="/settings/withdraw" className="inner-accordion-link-2" onClick={handleLinkClick}>Withdraw Points</NavLink>
                  </li>
                ) : (
                  <li className="accordion-item inner-accordion-item">
                    <NavLink to="/settings/add-balance" className="inner-accordion-link-2" onClick={handleLinkClick}>Add Balance</NavLink>
                  </li>
                )}
                  
                  {hasRole(['admin']) && (
                    <li className="accordion-item inner-accordion-item">
                      <NavLink to="/settings/maintenance" className="inner-accordion-link-2" onClick={handleLinkClick}>Maintenance Mode</NavLink>
                    </li>
                  )}
                  {hasRole(['admin']) && (
                    <li className="accordion-item inner-accordion-item">
                      <NavLink to="/settings/main" className="inner-accordion-link-2" onClick={handleLinkClick}>Main Setting</NavLink>
                    </li>
                  )}
                </ul>
              </AccordionPanel>
            )}

          </ul>
        </aside>

        {/* Tooltip */}
        <div className="custom-tooltip" style={{
          position:      'fixed',
          top:           tooltip.top,
          left:          tooltip.left,
          transform:     'translateY(-50%)',
          opacity:       tooltip.visible ? 1 : 0,
          pointerEvents: 'none',
          transition:    'opacity 0.15s ease',
          zIndex:        9999,
        }}>
          {tooltip.text}
        </div>
      </>
    );
  };

  export default Sidebar;